import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { backend } from '@/api/backend';
import { explainLine } from '@/api/explain';
import { ApiError } from '@/api/http';
import { Skeleton, Stamp, ReadFailed } from '@/components/ui';
import { ToolIcon } from '@/components/ToolIcon';
import { formatBytes } from '@/lib/format';
import { readable, say } from '@/lib/vocabulary';

/**
 * One reconstructed record, opened.
 *
 * The preview route returns a record field by field with the provenance of
 * each - stated by the source, derived by a rule, redacted, or unavailable -
 * plus its relationships, its attachments, and the fields that could not be
 * represented and why. Clicking a record in the graph did nothing at all, so
 * none of it was reachable.
 *
 * The unrepresented fields are shown rather than dropped: a record that
 * quietly omits what it could not carry is exactly the failure this product
 * exists to prevent.
 */

/**
 * Where a field's value came from, and how much weight it carries.
 *
 * All four rendered in the same faint grey, so "the source said this" and
 * "nothing is available for this" were equally quiet. Provenance is the whole
 * reason a buyer can trust a field, and the two that mean the value is absent
 * or withheld have to look different from the two that mean it is real.
 */
const PROVENANCE: Record<string, { text: string; className: string }> = {
  SOURCE_EXPLICIT: { text: 'stated by the source', className: 'text-ink-3' },
  DERIVED_DETERMINISTIC: { text: 'derived by a fixed rule', className: 'text-ink-3' },
  // A lawful redaction is the pipeline working as sold, and on a
  // de-identified corpus it is the common case. Toning it as attention paints
  // a correct delivery amber field after amber field until amber stops
  // meaning anything. What separates these two is that they carry no value,
  // which is the neutral tone, not the warning one.
  REDACTED: { text: 'redacted', className: 'text-mut' },
  UNAVAILABLE: { text: 'unavailable', className: 'text-mut' },
};

const UNREPRESENTED: Record<string, string> = {
  NOT_AN_EXACT_SCALAR: 'not a single exact value, so it is not claimed',
  EXCEEDS_FIELD_BOUND: 'too large to carry in a preview',
};

/**
 * Whether an attachment is actually there.
 *
 * A broken reference read exactly like a present one: same grey, same weight.
 * Three of these four mean the buyer does not get the file.
 */
const ATTACHMENT: Record<string, { text: string; className: string }> = {
  // `ok` means validated in this system. Nothing here checks a digest, so
  // green would turn a row that merely says PRESENT into an integrity claim,
  // which is the inference this section exists to refuse.
  PRESENT: { text: 'present', className: 'text-ink-2' },
  BROKEN_REFERENCE: { text: 'points at something absent', className: 'text-bad' },
  REDACTED: { text: 'redacted', className: 'text-mut' },
  TOO_LARGE: { text: 'too large to carry', className: 'text-warn' },
};

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

export function EntityPreview({ entityId, onClose }: { entityId: string; onClose: () => void }) {
  /**
   * The panel is a dialog, so it behaves like one.
   *
   * It had no role, no Escape, and no focus handling, and its only opener is a
   * click on a canvas that is aria-hidden. A keyboard operator could not open
   * a record at all, and one who arrived here had to tab forward through the
   * whole page to get out. Focus moves in on open and returns to whatever
   * opened it on close, so the graph does not lose the operator's place.
   */
  const panel = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    panel.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      opener?.focus?.();
    };
  }, [onClose]);

  const q = useQuery({
    queryKey: ['entity-preview', entityId],
    queryFn: ({ signal }) => backend.getEntityPreview(entityId, signal),
    retry: (n: number, e: unknown) => (e instanceof ApiError ? e.retryable && n < 2 : n < 2),
    staleTime: 300_000,
  });
  const d = ((q.data as { data?: unknown })?.data ?? q.data) as Record<string, unknown> | undefined;

  const fields = Array.isArray(d?.fields) ? (d!.fields as Record<string, unknown>[]) : [];
  const relationships = Array.isArray(d?.relationships) ? (d!.relationships as Record<string, unknown>[]) : [];
  const attachments = Array.isArray(d?.attachments) ? (d!.attachments as Record<string, unknown>[]) : [];
  const unrepresented = Array.isArray(d?.unrepresentedFields) ? (d!.unrepresentedFields as Record<string, unknown>[]) : [];

  return (
    <aside
      ref={panel}
      role="dialog"
      aria-modal="true"
      aria-label="Record"
      tabIndex={-1}
      className="absolute right-3 top-3 z-10 max-h-[calc(100%-1.5rem)] w-[26rem] max-w-[calc(100%-1.5rem)] overflow-y-auto rounded-md border border-line bg-panel shadow-pop focus:outline-none"
    >
      <div className="sticky top-0 flex items-center gap-2 border-b border-line bg-panel px-4 py-2.5">
        {d && <ToolIcon tool={str(d.toolSlug) || str(d.tool)} size={16} />}
        {/* The record carries a human title and its own integrity verdict,
            and the header drew neither: every message in a bucket opened as a
            panel headed "message", and a record that failed its checks looked
            identical to one that passed while every RELATIONSHIP below got a
            stamp. */}
        <span className="min-w-0 flex-1 truncate text-row text-ink">
          {d ? str(d.title) || say(str(d.entityType)) || 'record' : 'Record'}
        </span>
        {d && str(d.integrity) && (
          <Stamp
            tone={
              str(d.integrity) === 'VALIDATED'
                ? 'ok'
                : str(d.integrity) === 'BROKEN'
                  ? 'bad'
                  : str(d.integrity) === 'WARNING'
                    ? 'warn'
                    : 'mut'
            }
            className="flex-none"
          >
            {say(str(d.integrity))}
          </Stamp>
        )}
        <button
          onClick={onClose}
          aria-label="Close record"
          className="flex-none rounded p-1 text-ink-4 transition-colors duration-fast hover:bg-hov hover:text-ink"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>

      {q.isPending ? (
        <div className="space-y-2 p-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-5 w-full" />
          ))}
        </div>
      ) : q.isError || !d ? (
        <ReadFailed what={explainLine(q.error, 'read this record')} onRetry={() => void q.refetch()} />
      ) : fields.length === 0 &&
        relationships.length === 0 &&
        attachments.length === 0 &&
        unrepresented.length === 0 ? (
        /* Given the state of the reconstruction producer this is the likeliest
           outcome in production, and it rendered as a header over a blank
           panel, which reads as a screen that failed to draw. */
        <p className="px-4 py-3 text-small text-ink-3">
          This record resolved and carries no fields, relationships or attachments.
        </p>
      ) : (
        <>
          {fields.map((f) => (
            <div key={str(f.key)} className="border-b border-line px-4 py-2 last:border-0">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-caption text-ink-3">{str(f.label) || str(f.key)}</span>
                <span className={`ml-auto text-caption ${PROVENANCE[str(f.provenance)]?.className ?? 'text-bad'}`}>
                  {/* An unrecognised provenance is a doubt, so it must not
                      land on the calmest styling in the panel. */}
                  {PROVENANCE[str(f.provenance)]?.text ?? 'this build does not recognise where this value came from'}
                </span>
              </div>
              <span className="mt-0.5 block break-words text-small text-ink">
                {f.value === null || f.value === undefined ? '—' : readable(str(f.key), f.value)}
              </span>
            </div>
          ))}

          {d.fieldsTruncated === true && (
            <p className="border-b border-line px-4 py-2 text-caption text-warn">
              The backend truncated this record's fields, so it holds more than is shown.
            </p>
          )}

          {unrepresented.length > 0 && (
            <div className="border-b border-line px-4 py-2">
              <span className="text-caption font-semibold uppercase tracking-wider text-ink-3">Not claimed</span>
              {unrepresented.map((u) => (
                <p key={str(u.key)} className="mt-1 text-caption text-ink-3">
                  <span className="mono text-ink-2">{str(u.key)}</span>{' '}
                  {UNREPRESENTED[str(u.reason)] ?? say(str(u.reason))}
                </p>
              ))}
            </div>
          )}

          {relationships.length > 0 && (
            <div className="border-b border-line px-4 py-2">
              <span className="text-caption font-semibold uppercase tracking-wider text-ink-3">Connected to</span>
              {relationships.slice(0, 20).map((r) => (
                <div key={`${str(r.relationship)}:${str(r.entityId)}`} className="mt-1 flex flex-wrap items-baseline gap-x-2">
                  <span className="text-caption text-ink-3">{say(str(r.relationship))}</span>
                  <span className="min-w-0 flex-1 truncate text-small text-ink">{str(r.label)}</span>
                  <Stamp tone={str(r.integrity) === 'VALIDATED' ? 'ok' : str(r.integrity) === 'NOT_TESTED' ? 'mut' : 'bad'}>
                    {say(str(r.integrity))}
                  </Stamp>
                </div>
              ))}
              {relationships.length > 20 && (
                <p className="mt-1 text-caption text-ink-4">
                  Showing 20 of {relationships.length.toLocaleString()}.
                </p>
              )}
            </div>
          )}

          {attachments.length > 0 && (
            <div className="px-4 py-2">
              <span className="text-caption font-semibold uppercase tracking-wider text-ink-3">Attachments</span>
              {attachments.map((a) => (
                <div key={str(a.key)} className="mt-1 flex flex-wrap items-baseline gap-x-2">
                  <span className="min-w-0 flex-1 truncate text-small text-ink">{str(a.key)}</span>
                  <span className="mono text-caption text-ink-3">
                    {typeof a.sizeBytes === 'number' ? formatBytes(a.sizeBytes) : ''}
                  </span>
                  <span className={`text-caption ${ATTACHMENT[str(a.state)]?.className ?? 'text-bad'}`}>
                    {ATTACHMENT[str(a.state)]?.text ?? 'this build does not recognise this attachment state'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {/* Where this record came from. The provenance panel showed per-field
          provenance and dropped the record's own: which source version it was
          read out of, and by what parser. */}
      {d?.lineage != null && (
        <div className="border-t border-line px-4 py-2 text-caption text-ink-3">
          Read from source version{' '}
          <span className="mono text-ink-2">
            {str((d.lineage as Record<string, unknown>).sourceObjectVersionId).slice(0, 8)}
          </span>{' '}
          by <span className="mono text-ink-2">{str((d.lineage as Record<string, unknown>).parser)}</span>
          {str((d.lineage as Record<string, unknown>).location)
            ? ` at ${str((d.lineage as Record<string, unknown>).location)}`
            : ''}
          .
        </div>
      )}
    </aside>
  );
}
